import { forwardRef, type ReactNode } from 'react';
import Tooltip from './Tooltip';

export interface RailButtonProps {
  /** A 15px line icon from icons.tsx. */
  icon: ReactNode;
  /** The action's name, e.g. "Scratchpad". Drives the tooltip and aria-label. */
  label: string;
  shortcut?: string;
  pressed?: boolean;
  disabled?: boolean;
  /** Why it is dimmed. Appended to the tooltip so the dimming explains itself. */
  disabledReason?: string;
  count?: number;
  /** Widens the button and adds a caret, for triggers that open a menu. */
  hasMenu?: boolean;
  /** What the button opens, for triggers with no caret (More, Fields, Features). */
  haspopup?: 'menu' | 'dialog';
  expanded?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * One icon in the editor's rail. The name lives in the tooltip and the
 * aria-label, never on screen: seven labelled buttons is what pushed the old
 * header onto a second line in the first place.
 */
export const RailButton = forwardRef<HTMLButtonElement, RailButtonProps>(function RailButton(
  {
    icon,
    label,
    shortcut,
    pressed,
    disabled = false,
    disabledReason,
    count,
    hasMenu = false,
    haspopup,
    expanded,
    onClick,
    className = '',
  },
  ref,
) {
  const parts = [label];
  if (shortcut) parts.push(shortcut);
  if (disabled && disabledReason) parts.push(disabledReason);
  else if (pressed) parts.push('open');
  const description = parts.join(' · ');

  return (
    <Tooltip content={description}>
      <button
        ref={ref}
        type="button"
        // aria-disabled, not the disabled attribute: a dimmed icon still has to
        // be focusable and hoverable, or its reason can never be read.
        aria-disabled={disabled || undefined}
        // A control that says aria-expanded is a disclosure, not a toggle, so
        // it must not also claim aria-pressed. `pressed` still lights it.
        aria-pressed={expanded === undefined ? pressed : undefined}
        aria-haspopup={hasMenu ? 'menu' : haspopup}
        aria-expanded={expanded}
        aria-label={description}
        onClick={disabled ? undefined : onClick}
        className={`
          relative inline-flex items-center justify-center shrink-0
          h-7 ${hasMenu ? 'w-[38px]' : 'w-[30px]'} rounded-full
          transition-colors duration-150 select-none [-webkit-touch-callout:none]
          focus:outline-none focus-visible:shadow-focus
          ${
            pressed
              ? 'bg-card text-accent shadow-flare ring-1 ring-accent/25'
              : disabled
              ? 'opacity-40 cursor-not-allowed text-muted'
              : 'text-muted hover:text-fg hover:bg-elevated'
          }
          ${className}
        `}
      >
        {icon}
        {hasMenu && (
          // A CSS triangle rather than an SVG: at 3px an outlined caret is mush.
          <span
            aria-hidden
            className="absolute right-[7px] top-3 opacity-70"
            style={{
              borderStyle: 'solid',
              borderWidth: 3,
              borderColor: 'transparent',
              borderTopWidth: 3.5,
              borderTopColor: 'currentColor',
            }}
          />
        )}
        {count != null && count > 0 && (
          <span
            aria-hidden
            className={`
              absolute -top-[3px] -right-[2px] min-w-[14px] h-[14px] px-1 rounded-full
              font-mono text-[8px] font-semibold leading-[14px] text-center
              ${pressed ? 'bg-accent text-accent-fg' : 'bg-fg text-page'}
            `}
          >
            {count}
          </span>
        )}
      </button>
    </Tooltip>
  );
});

/** The pill the icons sit in. One rail, three families, hairlines between. */
export function Rail({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-px md:gap-[2px] p-[2px] rounded-full bg-elevated border border-line shrink-0">
      {children}
    </div>
  );
}

export function RailDivider() {
  return <span aria-hidden className="w-px h-4 bg-faint/40 mx-0.5 md:mx-[3px] shrink-0" />;
}
